import React, { useEffect } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { MODULE_SHARE_SHAKE_PATHS, useCurrentPathRouteConfig } from "@main/router/util/useCurrentPathRouteConfig";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import { RecommendUsRowItem } from "./InterjectedRow.model";
import * as Styled from "./InterjectedRows.styles";
import { InterjectedRowShell } from "./InterjectedRowShell";

export interface RecommendUsRowProps {
  row: RecommendUsRowItem;
  onDismiss: () => void;
}

/** Asks the user to spread the word, e.g. "1000 tickets in! - Share Shake". */
export const RecommendUsRow = ({ row, onDismiss }: RecommendUsRowProps) => {
  const { sendEvent } = useAnalytics();
  const navigate = useNavigate();
  const { selectedWorkspace } = useAppSelectionContext();
  const { moduleRoot } = useCurrentPathRouteConfig();
  const params = useParams<{ appKey: string }>();

  useEffect(() => {
    sendEvent("interjected_row_shown", { row_type: row.type, row_id: row.id, variant_id: row.variant_id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onActionClick = () => {
    sendEvent("interjected_row_cta_clicked", { row_type: row.type, row_id: row.id, variant_id: row.variant_id });

    const shareShakePath = moduleRoot ? MODULE_SHARE_SHAKE_PATHS[moduleRoot] : undefined;
    if (!shareShakePath) return;

    navigate(
      generatePath(shareShakePath, { workspaceSlug: selectedWorkspace?.slug ?? null, appKey: params.appKey ?? null }),
      { state: { title: row.title } },
    );
  };

  return (
    <InterjectedRowShell
      label={row.title}
      testId="recommend_us_row"
      onDismiss={onDismiss}
      descriptionTooltip={"Enjoying Shake? Help us grow by recommending it to a colleague"}
      action={<Styled.ActionButton onClick={onActionClick}>{row.action_label}</Styled.ActionButton>}
    >
      <Styled.Content>
        <Styled.IconBox>{row.icon}</Styled.IconBox>
        <Styled.Title p1>{row.title}</Styled.Title>
        <Styled.Description p2>{row.description}</Styled.Description>
      </Styled.Content>
    </InterjectedRowShell>
  );
};
